/* eslint-disable no-restricted-syntax */
import { FileTypeEnum } from '@constants';
import { HapvidaApi } from '@http';
import { generateUrlParams, splitFileNameAndExtension } from '@utils';

import type {
  GetCompanyDetailRequestDTO,
  GetCompanyDetailResponseDTO,
} from './dtos/GetCompanyDetailDTO';
import type { ListCompaniesResponseDTO } from './dtos/ListCompanyDTO';
import type { ListCompanyRequestDTO } from './dtos/ListCompanyRequestDTO';
import type {
  ListNewCompanyRequestDTO,
  ListNewCompanyResponseDTO,
} from './dtos/ListNewCompanyDTO';
import type {
  ResultIntegrationRequestDTO,
  ResultIntegrationResponseDTO,
} from './dtos/ResultIntegrationDTO';
import type { SendToProcessRequestDTO } from './dtos/SendToProcessDTO';
import type { UpdateResetupRequestDTO } from './dtos/UpdateSetupDTO';
import type { UploadSetupFileRequestDTO } from './dtos/UploadSetupFileDTO';

export default class CompanySetupService {
  private readonly endpoints = {
    getCompanies: () => `/api/v1/companies`,
    getNewCompanies: () => `/api/v1/companies/new-companies-paged`,
  };

  public async getCompanies({ ...options }: ListCompanyRequestDTO) {
    const params = generateUrlParams(options);

    const { data } = await HapvidaApi.get<ListCompaniesResponseDTO>(
      this.endpoints.getCompanies(),
      {
        params,
      },
    );

    return data.content;
  }

  public async getNewCompanies({ ...options }: ListNewCompanyRequestDTO) {
    const params = generateUrlParams(options);

    const { data } = await HapvidaApi.get<ListNewCompanyResponseDTO>(
      this.endpoints.getNewCompanies(),
      {
        params,
      },
    );

    return data.content;
  }

  public async uploadSetupFile({
    companyId,
    path,
    fileName,
  }: UploadSetupFileRequestDTO) {
    const [name, extension] = splitFileNameAndExtension(fileName);

    await HapvidaApi.patch(
      `/api/v1/companies/${companyId}/attachments/affiliates`,
      {
        file: path,
        extension,
        fileName: name,
        fileType: FileTypeEnum.CompanyAffiliates,
      },
    );
  }

  public async sendToProccess({ id }: SendToProcessRequestDTO) {
    const { data } = await HapvidaApi.patch<SendToProcessRequestDTO>(
      `/api/v1/companies/${id}/change-in-process`,
    );

    return data;
  }

  public async fetchCompanyDetail({
    id,
  }: GetCompanyDetailRequestDTO): Promise<GetCompanyDetailResponseDTO> {
    const { data } = await HapvidaApi.get<GetCompanyDetailResponseDTO>(
      `/api/v1/companies/${id}`,
    );

    return { content: { ...data.content } };
  }

  public async resultIntegrationCompany({
    id,
    limit,
    offset,
  }: ResultIntegrationRequestDTO): Promise<ResultIntegrationResponseDTO> {
    const { data } = await HapvidaApi.get<ResultIntegrationResponseDTO>(
      `/api/v1/companies/${id}/company-affiliates`,
      {
        params: {
          limit,
          offset,
        },
      },
    );

    return data;
  }

  public async updateIsReSetup({
    companyId,
    deleteAffiliatesDocuments,
    affiliates,
    parentCompany,
  }: UpdateResetupRequestDTO) {
    const { data } = await HapvidaApi.put(
      `/api/v1/companies/${companyId}/mass-update`,
      {
        deleteAffiliatesDocuments,
        affiliates,
        parentCompany,
      },
    );

    return data;
  }

  public async deleteAffiliates(companyId: string) {
    await HapvidaApi.delete(`/api/v1/companies/${companyId}/affiliates`);
  }
}
